import { Link } from "@tanstack/react-router";
import { ArrowLeft, Clock, Calendar as CalendarIcon } from "lucide-react";
import { PageShell } from "@/components/knmc/PageShell";
import type { ArticleDetail as ArticleDetailType, ArticleListItem } from "@/hooks/useArticles";

export default function ArticleDetail({ article, articles }: { article: ArticleDetailType; articles: ArticleListItem[] }) {
  if (!article) return null;

  const related = articles.filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <PageShell>
      <article className="bg-background">
        <div className="relative aspect-[21/9] w-full overflow-hidden bg-primary-soft">
          <img src={article.cover} alt={article.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 mx-auto max-w-4xl px-6 pb-10 text-white">
            <Link to="/health-articles" className="inline-flex items-center gap-1 text-sm text-white/80 hover:text-white">
              <ArrowLeft className="h-4 w-4" /> All articles
            </Link>
            <span className="mt-4 inline-block rounded-full bg-gold px-3 py-1 text-xs font-semibold text-primary">{article.category}</span>
            <h1 className="mt-4 font-display text-3xl font-semibold md:text-5xl">{article.title}</h1>
            <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-white/80">
              <span>{article.author}</span>
              <span className="flex items-center gap-1"><CalendarIcon className="h-3.5 w-3.5" />{article.date}</span>
              <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{article.readTime}</span>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-3xl px-6 py-16">
          <p className="font-display text-xl leading-relaxed text-foreground">{article.excerpt}</p>
          <div className="article-content-html">
            <div className="mt-8 space-y-6 text-lg leading-relaxed text-foreground/85 prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: article.content }} />
          </div>
          <div className="mt-12 rounded-2xl border border-border bg-primary-soft p-8">
            <p className="text-sm uppercase tracking-wider text-secondary">Medical disclaimer</p>
            <p className="mt-2 text-sm text-muted-foreground">
              This article is for general educational purposes only and is not a substitute for professional medical advice.
              Always consult a qualified physician for personal health concerns.
            </p>
          </div>
        </div>

        <section className="bg-surface py-16">
          <div className="mx-auto max-w-7xl px-6">
            <h2 className="mb-8 font-display text-2xl font-semibold text-primary">Related articles</h2>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((a) => (
                <Link
                  key={a.slug}
                  to={`/health-articles/${a.slug}`}
                  className="group overflow-hidden rounded-2xl border border-border bg-card transition-all hover:-translate-y-1 hover:shadow-elevated"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={a.cover} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">{a.category}</span>
                    <h3 className="mt-2 font-display font-space text-lg font-semibold text-foreground">{a.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </article>
    </PageShell>
  );
}
